import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  deleteTemplate,
  getTemplate,
  listTemplates,
  sendEnvelopeFromTemplate,
} from '../../services/templateService';
import UpgradeQuotaModal from '../billing/UpgradeQuotaModal';
import {
  isQuotaExceededError,
  getQuotaExceededMessage,
  isEnvelopeSendQuotaExceeded,
  getEnvelopeQuotaExceededMessage,
} from '../../utils/subscription';
import { formatDateTimeUTC } from '../../utils/dateFormat';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getCreatedByName = (template) => {
  const directName =
    template?.created_by_name ||
    template?.creator_name ||
    template?.owner_name ||
    template?.user_name ||
    template?.created_by_user_name ||
    '';
  if (directName) return directName;

  const creator =
    template?.created_by_user ||
    template?.created_by ||
    template?.creator ||
    template?.owner ||
    template?.user ||
    null;

  if (typeof creator === 'string') return creator;
  if (creator && typeof creator === 'object') {
    return (
      creator.name ||
      creator.full_name ||
      creator.display_name ||
      creator.email ||
      creator.username ||
      '-'
    );
  }
  return '-';
};

const Templates = () => {
  const navigate = useNavigate();
  const userSubscription = useSelector((state) => state.subscription?.userSubscription);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const getTemplateId = (template) => template?.id || template?.template_id || null;

  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [placeholderInputs, setPlaceholderInputs] = useState([]);
  const [resolvedVariableSigneesBase, setResolvedVariableSigneesBase] = useState([]);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [quotaModalMessage, setQuotaModalMessage] = useState('');

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listTemplates(statusFilter ? { status: statusFilter } : {});
      const items = response?.data?.templates || response?.data || [];
      setTemplates(Array.isArray(items) ? items : []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load templates.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const extractPlaceholders = (templateDetails) => {
    const set = new Set();

    const add = (v) => {
      if (v === undefined || v === null) return;
      const key = String(v).trim();
      if (key) set.add(key);
    };

    const fromArrays = [
      templateDetails?.required_placeholders,
      templateDetails?.variable_placeholders,
      templateDetails?.placeholders,
      templateDetails?.missing_placeholders,
    ];
    fromArrays.forEach((arr) => Array.isArray(arr) && arr.forEach(add));

    const recipients = templateDetails?.recipients || templateDetails?.template_recipients || [];
    if (Array.isArray(recipients)) {
      recipients.forEach((r) => {
        const placeholder = r?.signee_placeholder || r?.placeholder;
        const email = r?.email || r?.resolved_email;
        const name = r?.name || r?.resolved_name;
        const hasResolvedIdentity = Boolean(email && name);
        if (!hasResolvedIdentity) {
          add(placeholder);
        }
      });
    }

    return Array.from(set);
  };

  const resetSendModal = () => {
    setShowSendModal(false);
    setSelectedTemplateId(null);
    setSelectedTemplateName('');
    setPlaceholderInputs([]);
    setResolvedVariableSigneesBase([]);
  };

  const getVariableRecipientMappings = (templateDetails) => {
    const recipients =
      templateDetails?.template_recipients ||
      templateDetails?.recipients ||
      [];

    const list = Array.isArray(recipients) ? recipients : [];
    return list
      .filter((r) => {
        const placeholder = r?.signee_placeholder || r?.placeholder;
        const email = r?.email || r?.resolved_email;
        const name = r?.name || r?.resolved_name;
        const hasResolvedIdentity = Boolean(email && name);
        const isVariable = r?.is_variable === true || (Boolean(placeholder) && !hasResolvedIdentity);
        const envelopeRecipientId = r?.id ?? r?.recipient_id ?? r?.envelope_recipient_id ?? null;
        return isVariable && Boolean(placeholder) && envelopeRecipientId != null;
      })
      .map((r) => {
        const placeholder = r?.signee_placeholder || r?.placeholder;
        const envelopeRecipientId = r?.id ?? r?.recipient_id ?? r?.envelope_recipient_id ?? null;
        return {
          placeholder: String(placeholder).trim(),
          envelope_recipient_id: envelopeRecipientId,
        };
      })
      .filter((x) => x.placeholder && x.envelope_recipient_id != null);
  };

  const handleDelete = async (templateId) => {
    if (!templateId) return;
    const confirmed = window.confirm('Delete this template?');
    if (!confirmed) return;

    setDeletingId(templateId);
    try {
      await deleteTemplate(templateId);
      setSuccess('Template deleted successfully.');
      await loadTemplates();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError('Unauthenticated. Please log in again.');
      } else if (status === 404) {
        setError('Template not found.');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to delete template.');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleSendFromTemplate = async (template) => {
    const templateId = getTemplateId(template);
    if (!templateId) {
      setError('Template ID is missing.');
      return;
    }
    setError('');
    setSuccess('');
    setSendingId(templateId);
    try {
      const detailResponse = await getTemplate(templateId);
      const details = detailResponse?.data || {};
      const placeholders = extractPlaceholders(details);

      setSelectedTemplateId(templateId);
      setSelectedTemplateName(template.name || `Template #${templateId}`);
      setPlaceholderInputs(placeholders.map((p) => ({ placeholder: p, name: '', email: '' })));
      setResolvedVariableSigneesBase(getVariableRecipientMappings(details));
      setShowSendModal(true);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to prepare template draft.');
    } finally {
      setSendingId(null);
    }
  };

  const hasInvalidInputs = useMemo(() => {
    if (!placeholderInputs.length) return false;
    return placeholderInputs.some((row) => !row.name.trim() || !emailRegex.test(row.email.trim()));
  }, [placeholderInputs]);

  const buildVariableSigneesPayload = () => {
    return {
      variable_signees: placeholderInputs.map((row) => ({
        placeholder: row.placeholder,
        name: row.name.trim(),
        email: row.email.trim(),
      })),
      resolved_variable_signees: resolvedVariableSigneesBase
        .map((base) => {
          const input = placeholderInputs.find((p) => p.placeholder === base.placeholder);
          if (!input) return null;
          return {
            placeholder: base.placeholder,
            envelope_recipient_id: base.envelope_recipient_id,
            name: input.name.trim(),
            email: input.email.trim(),
          };
        })
        .filter(Boolean),
    };
  };

  const handleSendNow = async () => {
    if (!selectedTemplateId || hasInvalidInputs) return;
    if (isEnvelopeSendQuotaExceeded(userSubscription)) {
      const quotaMessage = getEnvelopeQuotaExceededMessage(userSubscription);
      setQuotaModalMessage(quotaMessage);
      setShowQuotaModal(true);
      setError(quotaMessage);
      return;
    }
    setError('');
    setSendingId(selectedTemplateId);
    try {
      const payload = {
        template_id: selectedTemplateId,
        send_mode: 'send',
        ...buildVariableSigneesPayload(),
      };

      const response = await sendEnvelopeFromTemplate(payload);
      const envelopeId = response?.data?.envelope_id;
      if (!envelopeId) {
        setError('Envelope ID was not returned by API.');
        return;
      }

      resetSendModal();
      navigate(`/envelopes/${envelopeId}/progress`);
    } catch (err) {
      if (isQuotaExceededError(err)) {
        setQuotaModalMessage(getQuotaExceededMessage(err));
        setShowQuotaModal(true);
        setError(getQuotaExceededMessage(err));
        return;
      }
      setError(err?.response?.data?.message || err?.message || 'Failed to send envelope from template.');
    } finally {
      setSendingId(null);
    }
  };

  const handleEditBeforeSend = async () => {
    if (!selectedTemplateId || hasInvalidInputs) return;
    setError('');
    setSendingId(selectedTemplateId);
    try {
      const payload = {
        template_id: selectedTemplateId,
        send_mode: 'draft',
        ...buildVariableSigneesPayload(),
      };

      const response = await sendEnvelopeFromTemplate(payload);
      const envelopeId = response?.data?.envelope_id;
      if (!envelopeId) {
        setError('Draft envelope ID was not returned by API.');
        return;
      }

      resetSendModal();
      navigate(`/create-envelope/${envelopeId}`);
    } catch (err) {
      if (isQuotaExceededError(err)) {
        setQuotaModalMessage(getQuotaExceededMessage(err));
        setShowQuotaModal(true);
        setError(getQuotaExceededMessage(err));
        return;
      }
      setError(err?.response?.data?.message || err?.message || 'Failed to create draft envelope from template.');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="page-content">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="mb-1">Templates</h5>
            <small className="text-muted">Create reusable templates for future envelopes.</small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ minWidth: 140 }}
            >
              <option value="draft">Draft</option>
              <option value="complete">Complete</option>
              <option value="">All</option>
            </Form.Select>
            <Button size="sm" onClick={() => navigate('/create-template')}>
              <i className="ri-add-line me-1" />
              Create Template
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {success && <div className="alert alert-success py-2">{success}</div>}

          {loading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th style={{ width: 72 }}>Serial No</th>
                  <th>Name</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Created by</th>
                  <th>Last updated at</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No templates found.
                    </td>
                  </tr>
                ) : (
                  templates.map((template, index) => {
                    const templateId = getTemplateId(template);
                    return (
                    <tr key={templateId || `${template.name || 'template'}-${template.subject || ''}`}>
                      <td>{index + 1}</td>
                      <td>{template.name || '-'}</td>
                      <td>{template.subject || '-'}</td>
                      <td>
                        <Badge bg={String(template.status).toLowerCase() === 'complete' ? 'success' : 'secondary'}>
                          {template.status || 'draft'}
                        </Badge>
                      </td>
                      <td>{getCreatedByName(template)}</td>
                      <td>{formatDateTimeUTC(template.updated_at || template.created_at)}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-success"
                          className="me-2"
                          disabled={!templateId || sendingId === templateId}
                          onClick={() => handleSendFromTemplate(template)}
                        >
                          {sendingId === templateId ? 'Sending...' : 'Use Template'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          disabled={!templateId}
                          onClick={() => navigate(`/create-template/${templateId}`)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={!templateId || deletingId === templateId}
                          onClick={() => handleDelete(templateId)}
                        >
                          {deletingId === templateId ? 'Deleting...' : 'Delete'}
                        </Button>
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showSendModal} onHide={resetSendModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Variable Signees</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Enter recipient details for template <strong>{selectedTemplateName}</strong>.
          </p>
          {placeholderInputs.length === 0 ? (
            <div className="text-muted">
              This template has no variable signees. You can send it directly or edit the draft before sending.
            </div>
          ) : (
            placeholderInputs.map((row, idx) => (
              <div key={row.placeholder} className="border rounded p-2 mb-2">
                <div className="small text-muted mb-2">Placeholder: {row.placeholder}</div>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">Name</Form.Label>
                  <Form.Control
                    value={row.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPlaceholderInputs((prev) => prev.map((item, i) => (i === idx ? { ...item, name: value } : item)));
                    }}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label className="mb-1">Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={row.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPlaceholderInputs((prev) => prev.map((item, i) => (i === idx ? { ...item, email: value } : item)));
                    }}
                  />
                </Form.Group>
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetSendModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={hasInvalidInputs || sendingId === selectedTemplateId}
            onClick={handleSendNow}
          >
            {sendingId === selectedTemplateId ? 'Working...' : 'Send From Template'}
          </Button>
          <Button
            variant="outline-primary"
            disabled={hasInvalidInputs || sendingId === selectedTemplateId}
            className="ms-2"
            onClick={handleEditBeforeSend}
          >
            Edit before sending
          </Button>
        </Modal.Footer>
      </Modal>

      <UpgradeQuotaModal
        show={showQuotaModal}
        onHide={() => setShowQuotaModal(false)}
        message={quotaModalMessage}
      />
    </div>
  );
};

export default Templates;
